const PAYABLE_STATUSES = ["open", "pending", "overdue", "expired"];
const LEGACY_RESTRICTED_STATUSES = [
  "SUSPENDED_OVERDUE", "CANCELLED_BY_ADMIN", "NON_CONVERTED", "RECURRENCE_PAUSED"
];

export const isBillingRestricted = (user, billing) => {
  if (billing?.enabled === true) {
    return billing.accessMode === "BILLING_ONLY";
  }
  return user?.company?.status === false || LEGACY_RESTRICTED_STATUSES.includes(user?.company?.billingStatus);
};

export const canPayInvoice = (invoice) => {
  if (!PAYABLE_STATUSES.includes(String(invoice?.status || "").toLowerCase())) return false;
  if (typeof invoice.canPay === "boolean") return invoice.canPay;
  // Legacy recurring card invoices still need a manual path after falling overdue.
  if (["expired", "overdue"].includes(String(invoice.status).toLowerCase())) return true;
  return !invoice.autoDebit || ["BOLETO", "PIX"].includes(invoice.billingType);
};

export const invoiceSummary = (invoices = []) => invoices.reduce((summary, invoice) => {
  const value = Number(invoice.value);
  if (!Number.isFinite(value)) return summary;
  if (invoice.status === "paid") {
    summary.paid += value;
    summary.total += value;
  }
  if (PAYABLE_STATUSES.includes(invoice.status)) {
    summary.pending += value;
    summary.total += value;
  }
  return summary;
}, { total: 0, paid: 0, pending: 0 });

export const formatBillingDate = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.split("-").reverse().join("/");
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Cuiaba" }).format(date);
};

export const billingNotice = (billing) => {
  if (!billing?.enabled) return null;
  const reason = billing.reason;
  if (billing.recurrenceState === "RECONCILING") {
    return { title: "Situação financeira em atualização", message: "Estamos conferindo as cobranças. Acompanhe a confirmação antes de realizar uma retomada; o último estado comprovado continua sendo considerado." };
  }
  if (["CANCELLED", "CUSTOMER_CANCELLED", "CANCELLED_BY_ADMIN"].includes(reason)) {
    return { title: "Contrato encerrado", message: billing.canRestart
      ? "Novas cobranças estão interrompidas. Você pode contratar novamente neste cadastro, sem um novo período de teste. As faturas anteriores permanecem no histórico."
      : "Novas cobranças estão interrompidas. Consulte o histórico financeiro ou entre em contato para uma nova contratação." };
  }
  if (reason === "NON_CONVERTED") {
    return { title: "Teste não convertido", message: billing.canRestart
      ? "O prazo para contratar terminou. Não serão geradas novas mensalidades sem uma nova contratação. Você pode contratar novamente neste cadastro, sem outro período de teste; as faturas anteriores permanecem no histórico."
      : "O prazo para contratar terminou. Não serão geradas novas mensalidades. A retomada depende de uma nova contratação; as cobranças canceladas permanecem no histórico." };
  }
  if (billing.recurrenceState === "PAUSE_PENDING") {
    return { title: "Pausa de cobranças em processamento", message: "A solicitação de pausa está sendo confirmada. As faturas existentes permanecem disponíveis para consulta." };
  }
  if (billing.recurrenceState === "INACTIVE") {
    return { title: "Novas cobranças pausadas", message: "As faturas já emitidas permanecem no Financeiro. Após regularizar as pendências, solicite a retomada da assinatura. O pagamento de uma fatura antiga não reativa a recorrência automaticamente." };
  }
  if (["RECURRENCE_PAUSED", "OVERDUE_LIMIT", "EXPLICIT_RESUME_REQUIRED"].includes(reason)) {
    return { title: "Recorrência em revisão", message: "O contrato exige uma verificação antes de gerar novas mensalidades. Consulte as pendências e acompanhe a confirmação da pausa ou as condições de retomada." };
  }
  if (["INITIAL_WINDOW_EXPIRED", "INITIAL_CANCELLED"].includes(reason)) {
    return { title: "Prazo de contratação encerrado", message: "O prazo para pagar a primeira fatura terminou. O encerramento está sendo conferido. Entre em contato para retomar a contratação." };
  }
  if (["TRIAL", "TRIAL_ACTIVE"].includes(reason) || (billing.accessMode === "FULL" && billing.trialEndsAt && !billing.paidThroughAt)) {
    return { title: "Período de teste", message: `Seu teste está disponível até ${formatBillingDate(billing.trialEndsAt)}. O vencimento da primeira fatura aparece abaixo.` };
  }
  if (["TRIAL_EXPIRED", "AWAITING_FIRST_PAYMENT"].includes(reason)) {
    const deadline = formatBillingDate(billing.payableUntil);
    if (reason === "AWAITING_FIRST_PAYMENT" && !billing.trialEndsAt) {
      return { title: "Aguardando primeiro pagamento", message: `A nova contratação não inclui período de teste. Use Pagar na primeira fatura para concluir no Asaas. ${deadline ? `O pagamento pode ser feito até ${deadline}. ` : ""}O acesso permanece limitado ao Financeiro até a confirmação do pagamento.` };
    }
    return { title: "Período de teste encerrado", message: `Seu acesso está limitado ao Financeiro. ${deadline ? `A primeira fatura pode ser paga até ${deadline}.` : "Consulte abaixo a primeira fatura disponível para contratação."} Essa janela de pagamento não amplia o período de teste.` };
  }
  if (billing.accessMode === "BILLING_ONLY") {
    return { title: "Acesso restrito ao Financeiro", message: "Consulte as pendências e as condições para liberar o acesso. A recorrência e o período de acesso são verificados separadamente." };
  }
  return null;
};
